import { CheckRounded } from '@mui/icons-material';
import { Box, ButtonBase, CircularProgress, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { FC } from 'react';

interface ToggleOptionCardProps {
    title: string;
    description?: string;
    selected: boolean;
    disabled?: boolean;
    busy?: boolean;
    onToggle: () => void;
}

const INACTIVE_BORDER = 'rgba(148, 163, 184, 0.28)';

/** Click-to-toggle card; the whole surface is the control and the active state is highlighted. */
export const ToggleOptionCard: FC<ToggleOptionCardProps> = ({
    title,
    description,
    selected,
    disabled = false,
    busy = false,
    onToggle,
}) => (
    <ButtonBase
        role="checkbox"
        aria-checked={selected}
        aria-busy={busy || undefined}
        disabled={disabled}
        focusRipple
        onClick={onToggle}
        sx={(theme) => ({
            width: '100%',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1.5,
            px: 1.75,
            py: 1.5,
            textAlign: 'left',
            borderRadius: '14px',
            border: '1px solid',
            borderColor: selected ? 'primary.main' : INACTIVE_BORDER,
            bgcolor: selected ? alpha(theme.palette.primary.main, 0.06) : 'background.paper',
            boxShadow: selected ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}` : 'none',
            transition: theme.transitions.create(
                ['border-color', 'background-color', 'box-shadow'],
                { duration: theme.transitions.duration.shorter }
            ),
            '&:hover': {
                borderColor: selected ? 'primary.main' : alpha(theme.palette.primary.main, 0.4),
                bgcolor: alpha(theme.palette.primary.main, selected ? 0.09 : 0.03),
            },
            '&.Mui-focusVisible': {
                boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.24)}`,
            },
            '&.Mui-disabled': { opacity: 0.6 },
        })}
    >
        <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
            <Typography
                variant="body2"
                sx={{
                    fontWeight: 600,
                    lineHeight: 1.4,
                    color: selected ? 'primary.dark' : 'text.primary',
                }}
            >
                {title}
            </Typography>
            {description ? (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ lineHeight: 1.4, display: 'block' }}
                >
                    {description}
                </Typography>
            ) : null}
        </Stack>
        <Box
            sx={(theme) => ({
                width: 22,
                height: 22,
                mt: 0.125,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                border: '1.5px solid',
                borderColor: selected ? 'primary.main' : 'rgba(148, 163, 184, 0.5)',
                bgcolor: selected ? 'primary.main' : 'transparent',
                color: 'primary.contrastText',
                transition: theme.transitions.create(['border-color', 'background-color'], {
                    duration: theme.transitions.duration.shorter,
                }),
            })}
        >
            {busy ? (
                <CircularProgress size={14} color={selected ? 'inherit' : 'primary'} />
            ) : selected ? (
                <CheckRounded sx={{ fontSize: 15 }} />
            ) : null}
        </Box>
    </ButtonBase>
);

export default ToggleOptionCard;
